import { Plus, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { loadWishlistBrandCatalogue, normalizeBrandName, type BrandSuggestion } from "./brand-data";
import BrandImage from "./BrandImage";
import { submitBrandSuggestion } from "./brand-suggestions";
import { translateInterfaceText, type InterfaceLanguage } from "./i18n";

type BrandSelectorProps = {
  selected: string[];
  onChange: (brands: string[]) => Promise<void>;
  variant?: "desktop" | "compact";
  heading?: string;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  hideSearchHeader?: boolean;
  allowSuggestions?: boolean;
  deferSuggestions?: boolean;
  language?: InterfaceLanguage;
};

export default function BrandSelector({ selected, onChange, variant = "desktop", heading = "Your favourite brand", searchQuery, onSearchQueryChange, hideSearchHeader = false, allowSuggestions = true, deferSuggestions = false, language = "en" }: BrandSelectorProps) {
  const tx = (value: string) => translateInterfaceText(value, language);
  const [internalQuery, setInternalQuery] = useState("");
  const query = searchQuery ?? internalQuery;
  const setQuery = (value: string) => {
    setInternalQuery(value);
    onSearchQueryChange?.(value);
  };
  const [suggestions, setSuggestions] = useState<BrandSuggestion[]>([]);
  const [catalogueBrandKeys, setCatalogueBrandKeys] = useState<Set<string>>(() => new Set());
  const [brandImages, setBrandImages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const normalizedQuery = normalizeBrandName(query);
  const sortedSelected = useMemo(
    () => [...selected].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base", numeric: true })),
    [selected],
  );
  const selectedKeys = useMemo(() => new Set(sortedSelected.map((name) => name.toLocaleLowerCase())), [sortedSelected]);
  const availableSuggestions = useMemo(
    () => suggestions.filter(({ name }) => !selectedKeys.has(name.toLocaleLowerCase())),
    [selectedKeys, suggestions],
  );
  const selectedExactMatch = normalizedQuery.length > 0 && selectedKeys.has(normalizedQuery.toLocaleLowerCase());
  const validCustomName = normalizedQuery.length >= 2 && /^[\p{L}\p{N}][\p{L}\p{N}&+'’.\- ]*$/u.test(normalizedQuery);
  const exactSuggestion = suggestions.find(({ name }) => name.toLocaleLowerCase() === normalizedQuery.toLocaleLowerCase());
  const searchActionName = exactSuggestion?.name ?? "";

  useEffect(() => {
    let active = true;
    setLoading(true);
    const timer = window.setTimeout(() => {
      // The Wishlist catalogue is the canonical image source. Load the full
      // set when browsing so selected brands never lose their shared artwork
      // merely because they fall outside the first result page.
      void loadWishlistBrandCatalogue().then((catalogue) => {
        if (!active) return;
        setCatalogueBrandKeys(new Set(catalogue.map(({ name }) => name.toLocaleLowerCase())));
        const searchKey = normalizeBrandName(query).toLocaleLowerCase();
        const items = catalogue
          .filter(({ name }) => !searchKey || name.toLocaleLowerCase().includes(searchKey))
          .slice(0, searchKey ? 16 : 500);
        setSuggestions(items);
        setBrandImages((current) => {
          const next = { ...current };
          items.forEach((brand) => { if (brand.imageUrl) next[brand.name.toLocaleLowerCase()] = brand.imageUrl; });
          return next;
        });
        setLoading(false);
      });
    }, query ? 180 : 0);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  const update = async (name: string, remove = false) => {
    const normalized = normalizeBrandName(name);
    if (!normalized || saving) return;
    setSaving(normalized);
    setError("");
    const next = remove
      ? sortedSelected.filter((item) => item.toLocaleLowerCase() !== normalized.toLocaleLowerCase())
      : selectedKeys.has(normalized.toLocaleLowerCase())
        ? sortedSelected
        : [...sortedSelected, normalized].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base", numeric: true }));
    try {
      await onChange(next);
      if (!remove) setQuery("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Brand choices could not be saved. Please try again.");
    } finally {
      setSaving("");
    }
  };

  const suggest = async () => {
    if (!validCustomName || suggesting) return;
    setSuggesting(true);
    setError("");
    setNotice("");
    try {
      if (!deferSuggestions) await submitBrandSuggestion(normalizedQuery);
      const pendingBrand = normalizedQuery;
      const next = selectedKeys.has(pendingBrand.toLocaleLowerCase())
        ? sortedSelected
        : [...sortedSelected, pendingBrand].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base", numeric: true }));
      await onChange(next);
      setNotice(deferSuggestions
        ? "Brand added to your list. It will be submitted for review when registration is completed."
        : "Brand submitted and added to your list. It is under review.");
      setQuery("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "This brand suggestion could not be submitted.");
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <section className={`${variant === "compact" ? "vista-section" : "content-card"} brand-selector brand-selector--${variant}`}>
      {!hideSearchHeader && <div className="brand-selector__heading">
        <h2>{tx(heading)}</h2>
      </div>}

      {!hideSearchHeader && <div className="product-search-row brand-search-row"><label className="brand-search product-search">
          <Search aria-hidden="true" />
          <input
            value={query}
            onChange={(event) => { setQuery(event.target.value); setError(""); setNotice(""); }}
            placeholder={tx("Search catalogue brands")}
            aria-label={tx("Search catalogue brands")}
            autoComplete="off"
          />
          {query && <button type="button" onClick={() => setQuery("")} aria-label={tx("Clear brand search")}><X /></button>}
        </label><button type="button" className="product-add-icon brand-add-icon" disabled={Boolean(saving) || !searchActionName || selectedExactMatch || selected.length >= 30} aria-label={searchActionName ? `${tx("Add")} ${searchActionName}` : tx("Choose a listed brand")} onClick={() => { if (searchActionName) void update(searchActionName); }}><Plus /></button></div>}

      {sortedSelected.length > 0 && <div className="selected-brands" aria-label={tx("Selected brands")}>
        <span>{tx("Selected brands")}</span>
        <div>{sortedSelected.map((name) => { const pending = !loading && !catalogueBrandKeys.has(name.toLocaleLowerCase()); return <button type="button" className={pending ? "selected-brand--pending" : undefined} key={name} disabled={Boolean(saving)} onClick={() => { void update(name, true); }}>{brandImages[name.toLocaleLowerCase()] && <BrandImage src={brandImages[name.toLocaleLowerCase()]} loading="lazy" decoding="async" />}<span>{name}{pending && <small>{tx("Under review")}</small>}</span><X aria-hidden="true" /></button>; })}</div>
      </div>}

      <div className="brand-results__label"><strong>{tx(query ? "Suggested matches" : "Lists of Brands")}</strong>{query && allowSuggestions && <span>{tx("Can’t find it? Suggest a new brand for review")}</span>}</div>
      <div className="brand-results" aria-live="polite">
        {loading && <p>{tx("Finding brand names…")}</p>}
        {!loading && allowSuggestions && validCustomName && !exactSuggestion && <button type="button" className="brand-result brand-result--custom" disabled={suggesting} onClick={() => { void suggest(); }}><span><Plus /><strong>{suggesting ? tx("Sending suggestion…") : `${tx("Suggest")} “${normalizedQuery}”`}</strong><small>{tx("Web App Admin will review its name and image")}</small></span></button>}
        {!loading && availableSuggestions.map((suggestion) => {
          return <button type="button" className="brand-result" key={suggestion.name} disabled={Boolean(saving) || selected.length >= 30} onClick={() => { void update(suggestion.name); }} aria-pressed={false}><span>{suggestion.imageUrl ? <BrandImage src={suggestion.imageUrl} alt={`${suggestion.name} product`} wrapperClassName="brand-result__image" loading="lazy" decoding="async" /> : <span className="brand-result__fallback" aria-hidden="true">{suggestion.name.slice(0, 1).toUpperCase()}</span>}<span className="brand-result__copy"><strong>{suggestion.name}</strong><small>{tx(suggestion.imageUrl ? "View retail item" : "Brand image not available")}</small></span></span><i><Plus /></i></button>;
        })}
        {!loading && availableSuggestions.length === 0 && selectedExactMatch && <p>{tx("This brand is already selected.")}</p>}
        {!loading && availableSuggestions.length === 0 && !selectedExactMatch && (!validCustomName || !allowSuggestions) && <p>{tx(query ? allowSuggestions ? "Enter at least two letters to search or suggest a brand." : "No listed catalogue brands match your search." : "All available brands are selected.")}</p>}
      </div>
      {error && <p className="brand-selector__error" role="alert">{error}</p>}
      {notice && <p className="brand-selector__notice" role="status">{tx(notice)}</p>}
    </section>
  );
}
